import { useEffect, useState } from "react";
import { Button, Form, Input, Skeleton, Space, Upload } from "antd";
import { useTranslation } from "react-i18next";
import { mdlFactSheetHotelAccountSaveRequest } from "../../models/service-models/fact-sheet/FactSheetHotelAccountSaveRequest";
import { useMutation, useQuery } from "react-query";
import { FactSheetService } from "../../services/FactSheetService";
import { mdlLanguage } from "../../models/ui-models/Language";
import { mdlFactSheetHotelAccountRequest } from "../../models/service-models/fact-sheet/FactSheetHotelAccountRequest";
import { mdlHotel } from "../../models/domain/Hotel";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { HotelService } from "../../services/HotelService";
import { CountrySelect } from "../common/CountrySelect";
import { CitySelect } from "../common/CitySelect";
import { RegionSelect } from "../common/RegionSelect";
import { SubRegionSelect } from "../common/SubRegionSelect";
import { enmFactSheetFileType } from "../../models/enums/FactSheetFileType";
import { mdlFactSheetFileUploadRequest } from "../../models/service-models/fact-sheet/FactSheetFileUploadRequest";
import { enmFactSheetFileSizeType } from "../../models/enums/FactSheetFileSizeType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faGlobe, faMobile, faPhone, faPrint, faUpload, faUser } from "@fortawesome/free-solid-svg-icons";

export type HotelAccountSettingsProps = {
    selectedLanguage?: mdlLanguage;
    selectedHotel?: string;
    readonly?: boolean;
    onDataLoad?: (data: mdlHotel) => void;
}

export const HotelAccountSettings = (function HotelAccountSettings(props: HotelAccountSettingsProps) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [request, setRequest] = useState<mdlFactSheetHotelAccountSaveRequest>();
    const [countryId, setCountryId] = useState<string>();
    const [cityId, setCityId] = useState<string>();
    const [regionId, setRegionId] = useState<string>();
    const [parentRegionId, setParentRegionId] = useState<string>();

    useEffect(() => {
        if (props.selectedHotel) {
            setRequest(undefined);
            setCityId(undefined);
            setParentRegionId(undefined);
            setRegionId(undefined);
            setCountryId(undefined);
            form.setFieldValue("categoryName", t("common.loading"))
            setMutateHotelAccount(props.selectedHotel, (hotelAccount: mdlHotel) => {
                const _request = {
                    categoryId: hotelAccount.categoryId,
                    hotelId: props.selectedHotel,
                    localName: hotelAccount.hotelName,
                    groupName: hotelAccount.groupName,
                    roomCount: hotelAccount.roomCount,
                    bedCount: hotelAccount.bedCount,
                    postalCode: hotelAccount.postalCode,
                    address: hotelAccount.address,
                    countryId: hotelAccount.countryId,
                    cityId: hotelAccount.cityId,
                    parentRegionId: hotelAccount.parentRegionId,
                    regionId: hotelAccount.regionId,
                    hmsService: hotelAccount.hmsService,
                    authorizedPerson: hotelAccount.authorizedPerson,
                    authorizedPersonMail: hotelAccount.authorizedPersonMail,
                    sellAuthorizedPerson: hotelAccount.sellAuthorizedPerson,
                    sellAuthorizedPersonMail: hotelAccount.email2,
                    webSite: hotelAccount.webSite,
                    phone: hotelAccount.phone,
                    mobilePhone: hotelAccount.mobilePhone,
                    fax: hotelAccount.fax,
                    lat: hotelAccount.lat,
                    lon: hotelAccount.lon,
                    bankAccount: hotelAccount.bankAccount,
                    giataCode: hotelAccount.giataCode,
                    paximumId: hotelAccount.paximumId,
                    hotel2SejourPlusStatus: hotelAccount.hotel2SejourPlusStatus,
                    hotel2SejourPlusStatusText: hotelAccount.hotel2SejourPlusStatus ? t("common.yes") : t("common.no")
                } as mdlFactSheetHotelAccountSaveRequest;
                setRequest(_request);
                setCityId(_request.cityId);
                setParentRegionId(_request.parentRegionId);
                setRegionId(_request.regionId);
                setCountryId(_request.countryId);
                form.setFieldsValue(_request);
                props.onDataLoad?.(hotelAccount);
            })
        }
    }, [props.selectedHotel, props.selectedLanguage, t]);

    const { data: categoryList, isLoading: categoryIsLoading, refetch: refetchCategoryList, isError: isErrorCategoryList } = useQuery(
        ["getHotelCategoryList", props.selectedHotel],
        () => {
            return HotelService.CategoryList({ currentLanguage: props.selectedLanguage?.value });
        },
        {
            staleTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!props.selectedHotel && request != null && request.categoryId != null,
            onSuccess(data) {
                if (data && data.body && data.body.data) {
                    const category = data.body.data.find(x => x.id == request?.categoryId)
                    if (category) {
                        form.setFieldValue("category", category.id)
                        form.setFieldValue("categoryName", category.name)
                    }
                }
            },
        }
    );

    const { mutateAsync: saveHotelAccount, isLoading: mutateLoading, data: mutateData } = useMutation(
        (data: mdlFactSheetHotelAccountSaveRequest) => FactSheetService.HotelAccountSave(data),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            }
        }
    );

    const getHotelAccount = useMutation({
        mutationFn: (hotelId: string) => {
            return FactSheetService.GetHotelAccount({ hotelId: hotelId, currentLanguage: props.selectedLanguage?.value, } as mdlFactSheetHotelAccountRequest)
        }
    })

    const setMutateHotelAccount = async (hotelId: string, fnSuccess: Function) => {
        const data = await getHotelAccount.mutateAsync(hotelId)
        if (data && data.body && data.body.data) {
            fnSuccess(data.body.data);
        }
    }

    const getFileUploadService = useMutation({
        mutationFn: (request: FormData) => {
            return FactSheetService.FileUpload(request)
        }
    })

    const customRequest = async (imgSizeType: any, fileType: any, options: any) => {
        const { onSuccess, onError, file, onProgress } = options;

        let formData = new FormData();
        formData.append("file", file)

        if (props.selectedHotel)
            formData.append("hotelId", props.selectedHotel);
        if (imgSizeType)
            formData.append("hotelId", imgSizeType.toString());
        if (fileType)
            formData.append("fileType", fileType.toString());
        if (fileType)
            formData.append("fileName", fileType.toString());

        formData.append("subFolder", "");

        const request = {
            subFolder: "",
            fileName: fileType.toString(),
            hotelId: props.selectedHotel,
            imgSizeType: imgSizeType,
            imgType: fileType
        } as mdlFactSheetFileUploadRequest;

        formData.append("request", JSON.stringify(request));

        getFileUploadService.mutate(formData, {
            onSuccess(data) {
                if (data && data.body && data.header.success) {
                    toast.success(t("common.success"));
                    options.onSuccess("ok")
                } else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                    options.onError("ok")
                }
            }
        })
    };
    return (
        <>
            {
                !request &&
                <>
                    <Skeleton />
                </>
            }
            {
                request &&
                <Form
                    form={form}
                    initialValues={request}
                    name="fact-sheet-hotel-account-settings"
                    layout="vertical"
                    onFinish={async (values) => await saveHotelAccount(values)}
                    autoComplete="off"
                    disabled={props.readonly}
                >
                    <Form.Item<any>
                        name={'hotelId'}
                        hidden={true}
                    >
                        <Input disabled />
                    </Form.Item>
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.hotelName")}
                                name={'localName'}
                            >
                                <Input disabled />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.groupName")}
                                name={'groupName'}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.category")}
                                name={'category'}
                                hidden={true}
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.category")}
                                name={'categoryName'}
                            >
                                <Input disabled />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.paximumId")}
                                name={'paximumId'}
                            >
                                <Input disabled />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.giataCode")}
                                name={'giataCode'}
                            >
                                <Input disabled />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.h2sPlusAccount")}
                                name={'hotel2SejourPlusStatus'}
                                hidden={true}
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.h2sPlusAccount")}
                                name={'hotel2SejourPlusStatusText'}
                            >
                                <Input disabled />
                            </Form.Item>
                        </div>
                        <div className="col-md-6 py-2">
                            <div className="form-group pt-4 pb-2" >
                                <a href="http://www.hotel2sejour.com" target="blank">{t("factSheet.hotelAccountSetting.detailInformatinText")}</a>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.address")}
                                name={'address'}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.postCode")}
                                name={'postalCode'}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">

                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.country")}
                                name="countryId"
                                rules={[{ required: true, message: t("common.required") }]}
                            >
                                <CountrySelect onChange={(value: string) => { form.setFieldsValue({ countryId: value }); setCountryId(value); form.setFieldsValue({ cityId: undefined }) }} />
                            </Form.Item>

                        </div>
                        <div className="col-md-6">

                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.city")}
                                name="cityId"
                                rules={[{ required: true, message: t("common.required") }]}
                            >
                                <CitySelect countryId={countryId} onChange={(value: string) => { form.setFieldsValue({ cityId: value }); setCityId(value) }} selectedLanguage={props.selectedLanguage} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.place")}
                                name={'parentRegionId'}
                            >
                                <RegionSelect cityId={cityId} onChange={(value: string) => { form.setFieldsValue({ parentRegionId: value }); setParentRegionId(value) }} selectedLanguage={props.selectedLanguage} />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.subPlace")}
                                name={'regionId'}
                            >
                                <SubRegionSelect parentRegionId={parentRegionId} onChange={(value: string) => form.setFieldsValue({ regionId: value })} selectedLanguage={props.selectedLanguage} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.frontOfficeProgram")}
                                name={'hmsService'}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                        <div className="col-md-6 py-2">
                            <div className="form-group pt-4 pb-2" >
                                <a href="http://www.hotel2sejour.com" target="blank">{t("factSheet.hotelAccountSetting.detailInformatinText")}</a>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.reservationManager")}
                                name={'authorizedPerson'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faUser} />} />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.email")}
                                name={'authorizedPersonMail'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faEnvelope} />} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.salesManager")}
                                name={'sellAuthorizedPerson'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faUser} />} />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.email")}
                                name={'sellAuthorizedPersonMail'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faEnvelope} />} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.phone")}
                                name={'phone'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faPhone} />} />

                            </Form.Item>
                        </div>

                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.mobilePhone")}
                                name={'mobilePhone'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faMobile} />} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.fax")}
                                name={'fax'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faPrint} />} />
                            </Form.Item>
                        </div>

                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.webSite")}
                                name={'webSite'}
                            >
                                <Input prefix={<FontAwesomeIcon icon={faGlobe} />} />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.roomCount")}
                                name={'roomCount'}
                            >
                                <Input />
                            </Form.Item>
                        </div>

                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.bedCount")}
                                name={'bedCount'}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <Form.Item<any>
                            label={t("factSheet.hotelAccountSetting.bankAccount")}
                            name={'bankAccount'}
                        >
                            <Input />
                        </Form.Item>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.logo")}
                                name={'logo'}
                            >
                                <Upload customRequest={(options: any) => { customRequest(enmFactSheetFileSizeType.Logo, enmFactSheetFileType.Logo, options) }} maxCount={1} {...props}>
                                    <Button icon={<FontAwesomeIcon icon={faUpload} />}>Click to Upload</Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.confirmationSignature")}
                                name={'confirmStamp'}
                            >
                                <Upload customRequest={(options: any) => { customRequest(enmFactSheetFileSizeType.Custom, enmFactSheetFileType.ConfirmStamp, options) }} maxCount={1} {...props}>
                                    <Button icon={<FontAwesomeIcon icon={faUpload} />}>Click to Upload</Button>
                                </Upload>
                            </Form.Item>
                        </div>

                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.notConfirmationSignature")}
                                name={'unConfirmStamp'}
                            >
                                <Upload customRequest={(options: any) => { customRequest(enmFactSheetFileSizeType.Custom, enmFactSheetFileType.UnConfirmStamp, options) }} maxCount={1} {...props}>
                                    <Button icon={<FontAwesomeIcon icon={faUpload} />}>Click to Upload</Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.latitude")}
                                name={'lat'}
                            >
                                <Input />
                            </Form.Item>
                        </div>

                        <div className="col-md-6">
                            <Form.Item<any>
                                label={t("factSheet.hotelAccountSetting.longitude")}
                                name={'lon'}
                            >
                                <Input />
                            </Form.Item>
                        </div>

                        <div className="col-12">
                            {!props.readonly && (
                                <Form.Item>
                                    <Space>
                                        <Button type="primary" htmlType="submit">{t("common.save")}</Button>
                                    </Space>
                                </Form.Item>
                            )}
                        </div>
                    </div>

                </Form >
            }
        </>
    )
})